import React, { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { FaGithub, FaLinkedin, FaEnvelope, FaDownload, FaChevronRight, FaCode, FaDatabase, FaCloud, FaRocket } from 'react-icons/fa'

const HeroSection = () => {
  const profileImage = '/image.gif'
  const ref = useRef(null)
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  })
  
  const y = useTransform(scrollYProgress, [0, 1], [0, -50])
  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.95])
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0.8])

  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 2,
    duration: Math.random() * 3 + 2,
    delay: Math.random() * 2,
  }))

  return (
    <motion.aside
      ref={ref}
      className="md:fixed md:left-4 md:top-4 md:bottom-4 md:w-80 w-full relative z-50 flex flex-col"
      style={{ y, scale, opacity }}
      initial={{ x: -100, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <motion.div
        className="glass rounded-3xl p-6 md:h-full h-auto flex flex-col shadow-2xl md:overflow-y-auto overflow-visible relative"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <motion.div
          className="absolute inset-0 rounded-3xl opacity-15 pointer-events-none"
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6, #ec4899, #3b82f6)',
            backgroundSize: '400% 400%',
          }}
          animate={{
            backgroundPosition: ['0% 50%', '50% 100%', '100% 50%', '50% 0%', '0% 50%'],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear',
          }}
        />

        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full pointer-events-none"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: particle.size,
              height: particle.size,
              background: `hsl(${particle.id * 18}, 70%, 60%)`,
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 0.6, 0],
              scale: [0, 1, 0],
              y: [0, -100, 0],
            }}
            transition={{
              duration: particle.duration * 3,
              repeat: Infinity,
              delay: particle.delay,
              ease: 'easeInOut',
            }}
          />
        ))}

        <motion.div
          className="relative mb-6"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.div className="relative">
            <div className="relative rounded-2xl overflow-hidden border-2 border-white/10 backdrop-blur-sm">
              <img
                src={profileImage}
                alt="Anisur Rahaman Sakib"
                className="w-full md:max-w-full max-w-sm mx-auto object-cover"
              />
              
              <motion.div
                className="absolute bottom-2 left-2 bg-green-500/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-2 shadow-lg"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.4, delay: 0.5 }}
              >
                <motion.div
                  className="w-2 h-2 bg-white rounded-full"
                  animate={{
                    scale: [1, 1.2, 1],
                    boxShadow: [
                      '0 0 0 0 rgba(34, 197, 94, 0.4)',
                      '0 0 0 6px rgba(34, 197, 94, 0)',
                    ],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                <span className="text-white text-xs font-medium">Available</span>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <motion.span
            className="text-blue-400 text-sm font-medium inline-block"
            animate={{
              rotate: [0, 10, 0, -10, 0],
            }}
            transition={{
              duration: 0.5,
              delay: 2,
              repeat: Infinity,
              repeatDelay: 3,
            }}
          >
            👋 Hello, I'm
          </motion.span>
        </motion.div>

        <motion.h1
          className="text-3xl md:text-3xl font-bold mb-2 leading-tight cursor-default"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <motion.span
            className="inline-block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            Anisur Rahaman
          </motion.span>
          <br />
          <motion.span
            className="inline-block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
            animate={{ y: [0, -3, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          >
            Sakib
          </motion.span>
        </motion.h1>

        <motion.h2
          className="text-lg md:text-lg text-gray-300 font-medium mb-4"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <span className="relative inline-block">
            Senior Software Engineer
            <motion.span
              className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500"
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 1, delay: 0.6 }}
            />
          </span>
        </motion.h2>

        <motion.p
          className="text-sm md:text-sm text-gray-400 mb-4 leading-relaxed"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          Building scalable web applications and leading development teams for over a decade.
          Expert in PHP, JavaScript, DevOps, and creating seamless user experiences.
        </motion.p>

        <motion.div
          className="flex flex-wrap gap-2 mb-6"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          {[
            { name: 'Full-Stack', icon: FaCode },
            { name: 'DevOps', icon: FaCloud },
            { name: 'Agile', icon: FaRocket },
            { name: 'System Design', icon: FaDatabase },
          ].map((skill, index) => (
            <motion.span
              key={skill.name}
              className="px-3 py-1 glass rounded-full text-xs text-gray-300 cursor-default flex items-center gap-2"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.4,
                delay: 0.7 + index * 0.15,
              }}
              whileTap={{ scale: 0.9 }}
            >
              <skill.icon className="text-blue-400" size={10} />
              {skill.name}
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10"
                animate={{ opacity: [0, 0.3, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
              />
            </motion.span>
          ))}
        </motion.div>

        <motion.div
          className="flex gap-3 mb-auto"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          {[
            { icon: FaEnvelope, href: 'mailto:anisursakib@gmail.com', label: 'Email', color: 'from-blue-500 to-cyan-500' },
            { icon: FaGithub, href: 'https://github.com', label: 'GitHub', color: 'from-gray-600 to-gray-800' },
            { icon: FaLinkedin, href: 'https://linkedin.com', label: 'LinkedIn', color: 'from-blue-600 to-blue-800' },
          ].map((social, index) => (
            <motion.a
              key={index}
              href={social.href}
              target={social.href.startsWith('http') ? '_blank' : undefined}
              rel="noopener noreferrer"
              className="w-10 h-10 glass rounded-full flex items-center justify-center relative overflow-hidden group"
              whileTap={{ scale: 0.9 }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 0.4,
                delay: 0.8 + index * 0.1,
              }}
            >
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${social.color}`}
                initial={{ opacity: 0 }}
                whileTap={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              />
              <motion.div className="relative z-10" whileTap={{ rotate: 360 }} transition={{ duration: 0.5 }}>
                <social.icon className="text-gray-300 transition-colors" />
              </motion.div>
              <motion.span
                className="absolute inset-0 rounded-full border-2 border-current opacity-0"
                whileTap={{ scale: 1.5, opacity: 1, transition: { duration: 0.5 } }}
                style={{ color: social.color.split(' ')[1] }}
              />
            </motion.a>
          ))}
        </motion.div>

        <motion.div
          className="pt-4 border-t border-white/10 space-y-3"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
        >
          <motion.a
            href="#contact"
            className="group w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center gap-2 text-white font-medium relative overflow-hidden"
            whileTap={{ scale: 0.97 }}
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 1 }}
          >
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500"
              initial={{ x: '-100%' }}
              whileTap={{ x: '0%' }}
              transition={{ duration: 0.3 }}
            />
            <span className="relative flex items-center gap-2">
              Get In Touch
              <motion.div animate={{ x: [0, 5, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                <FaChevronRight />
              </motion.div>
            </span>
          </motion.a>

          <motion.a
            href="#projects"
            className="group w-full px-6 py-3 glass rounded-xl flex items-center justify-center gap-2 text-white font-medium hover:bg-white/20 transition-all duration-300 relative overflow-hidden"
            whileTap={{ scale: 0.97 }}
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 1.1 }}
          >
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20"
              initial={{ x: '-100%' }}
              whileTap={{ x: '0%' }}
              transition={{ duration: 0.3 }}
            />
            <span className="relative flex items-center gap-2">
              View Projects
              <motion.div animate={{ y: [0, -3, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                <FaDownload />
              </motion.div>
            </span>
          </motion.a>
        </motion.div>

        <motion.div
          className="absolute -top-2 -right-2 w-4 h-4 bg-blue-500 rounded-full opacity-50 blur-sm"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.5, 0.8, 0.5],
            y: [0, -5, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute -bottom-2 -left-2 w-3 h-3 bg-pink-500 rounded-full opacity-50 blur-sm"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.5, 0.8, 0.5],
            y: [0, 5, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
        />
      </motion.div>
    </motion.aside>
  )
}

export default HeroSection